import React, { useState, useEffect } from 'react';
import { useAuth } from '../../helpers/AuthContext';
import Table from '../Table';

const getTitle = (pv, products) => {
    if (pv.path === '/') return 'Home';
    if (pv.path === '/store') return 'Store';
    if (pv.path === '/blog') return 'Blog';
    if (pv.path === '/store/buy') return 'Checkout';
    if (pv.path.startsWith('/store/detail/')) {
        const productId = pv.path.split('/')[3];
        const product = products.find(p => p.uuid === productId);
        if (product) return product.name;
    }
    if (pv.path.startsWith('/page/')) {
        const pageSlug = pv.path.split('/')[2];
        return pageSlug.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
    if (pv.path.startsWith('/blog/')) {
        const pageSlug = pv.path.split('/')[2];
        return pageSlug.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
    const product = products.find(p => p.uuid === pv.path);
    if (product) return product.name;
    return pv.path;
};

// Analytics buckets are calendar dates stored as 00:00Z (server/helpers/makeAggregateTimestamp.js),
// so group and label them in UTC. Formatting in the browser's zone shows the previous evening for
// every Americas viewer: Oct 7 00:00Z renders as "10/6".
const bucketKey = (timestamp) => {
    const date = new Date(timestamp);
    return Number.isNaN(date.getTime()) ? null : date.toISOString().slice(0, 10);
};

const formatBucketDate = (key, options) =>
    new Date(`${key}T00:00:00Z`).toLocaleDateString(undefined, { timeZone: 'UTC', ...options });

const getUrl = (path, storefrontUrl) => {
    if (path.startsWith('http')) return path;
    return storefrontUrl + path;
};

export default function AweChart({ events: eventsProp, products: productsProp, storefrontUrl: storefrontUrlProp }) {
    const auth = useAuth();
    // When `events` is passed (StorefrontAnalytics feeds pre-filtered slices), render from props
    // and skip the fetch. With no props the Dashboard-home mount (`#r-awe-chart`) self-fetches
    // the combined dataset exactly as before.
    const usingProps = eventsProp !== undefined;
    const [fetchedData, setFetchedData] = useState(null);
    const [loading, setLoading] = useState(!usingProps);
    const [error, setError] = useState(null);
    const [tableExpanded, setTableExpanded] = useState(false);

    useEffect(() => {
        if (usingProps) return;
        auth.fetch(`${auth.API_URL}/v1/seller/analytics`)
            .then(r => r.json())
            .then(json => {
                if (json.meta?.success) {
                    setFetchedData(json.data);
                } else {
                    setError('Failed to load analytics.');
                }
            })
            .catch(() => setError('Failed to load analytics.'))
            .finally(() => setLoading(false));
    }, [auth.API_URL, usingProps]);

    if (loading) return <div>Loading analytics...</div>;
    if (error) return <div>{error}</div>;

    const data = usingProps
        ? { events: eventsProp, products: productsProp || [], storefrontUrl: storefrontUrlProp || '' }
        : fetchedData;

    if (!data) return null;

    const { events, products, storefrontUrl } = data;
    const filtered = events;

    // Group by date bucket
    const tsMap = {};
    filtered.forEach(ev => {
        const key = bucketKey(ev.timestamp);
        if (!key) return;
        if (!tsMap[key]) tsMap[key] = [];
        tsMap[key].push(ev);
    });

    // keys are YYYY-MM-DD, so a plain sort is chronological
    const tsList = Object.keys(tsMap).sort();

    let maxCount = 0;
    tsList.forEach(ts => {
        const total = tsMap[ts].reduce((acc, pv) => acc + pv.count, 0);
        if (total > maxCount) maxCount = total;
        tsMap[ts].sort((a, b) => a.count - b.count);
    });

    // Build table rows
    const tableRows = [];
    tsList.forEach((ts, tsIdx) => {
        tsMap[ts].forEach((pv, i) => {
            tableRows.push({
                key: `${tsIdx}-${i}`,
                date: formatBucketDate(ts),
                title: getTitle(pv, products),
                path: pv.path,
                count: pv.count,
                type: pv.type,
            });
        });
    });

    return (
        <div>
            <div className="stacked-chart">
                {tsList.map(ts => {
                    const totalCount = tsMap[ts].reduce((acc, pv) => acc + pv.count, 0);
                    const columnHeight = maxCount > 0 ? Math.floor((totalCount / maxCount) * 100) : 0;
                    return (
                        <div className="column" key={ts}>
                            <ul style={{ height: `${columnHeight}%` }}>
                                {tsMap[ts].map((pv, i) => {
                                    const itemHeight = totalCount > 0 ? Math.floor((pv.count / totalCount) * 100) : 0;
                                    return (
                                        <li key={i} style={{ height: `${itemHeight}%` }}>
                                            <a href={getUrl(pv.path, storefrontUrl)} target="_blank" rel="noreferrer">
                                                x{pv.count} @ {getTitle(pv, products)}
                                            </a>
                                        </li>
                                    );
                                })}
                            </ul>
                            <label title={formatBucketDate(ts, { dateStyle: 'full' })}><small>{formatBucketDate(ts)}</small></label>
                            <label><strong>{totalCount}</strong></label>
                        </div>
                    );
                })}
            </div>
            <div>
                <a style={{cursor: "pointer", marginTop: '1rem'}} onClick={() => setTableExpanded(x => !x)}>
                    <strong>{tableExpanded ? '▲ Hide Table' : '▼ Show Data Table'}</strong>
                </a>
                {tableExpanded && (
                    <Table
                        data={tableRows}
                        columns={[
                            { key: 'date', header: 'Date' },
                            { key: 'title', header: 'Page' },
                            { key: 'count', header: 'Count', sortable: true },
                            { key: 'type', header: 'Type' },
                        ]}
                        keyField="key"
                        sortable={true}
                        pagination={true}
                        itemsPerPage={20}
                    />
                )}
            </div>
        </div>
    );
}
